
import { useDeleteTable } from '@/views/editor/composable/table/useDeleteTable.ts'
import { storeToRefs } from 'pinia'
import { useEditorStore } from '@/stores/editor.ts'
import { deletePrefix, getNodePath, getNodeFromPath } from '@/views/editor/composable/useNoteUtils.ts'

export class UseSelection {
	private element: any
	private targetNode: Node | null = null
	private currentOffset = 0
	private targetOffset = 0

	private selection: Selection | any

	constructor() {
		this.element = null
		this.targetNode = null
		this.currentOffset = 0
		this.targetOffset = 0

		// new UseSelection 으로 호출시 모든 변수 초기화
		this.selection = window.getSelection()
	}

	public resetChangeSelection() {
		const { changeRange } = storeToRefs(useEditorStore())
		changeRange.value = null
	}

	public setChangeSelection(offset: number = -1, endOffset: number = -1, id: string = '') {
		const { changeRange, selector } = storeToRefs(useEditorStore())
		// 조합 중이어도 커서 복원 정보는 저장하되, 내용 동기화는 조합 종료 후 수행
		const selection: any = window.getSelection()
		if (selection && selection.rangeCount > 0) {
			const range = selection.getRangeAt(0)
			const nodePath = getNodePath(range.startContainer)
			const endNodePath = getNodePath(range.endContainer)
			const tmpOffset = offset !== -1 ? offset : range.startOffset + 1
			const tmpEndOffset = endOffset !== -1 ? endOffset : range.endOffset + 1
			const getScopeId = (node: Node) => {
				const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement
				if (!el || !(el instanceof HTMLElement)) return ''
				const td = el.closest('td')
				if (td && !td.classList.contains('number--line') && td.id) {
					const result = deletePrefix(td.id)
					return result
				}
				const block = el.closest('.editor--block')
				return block ? deletePrefix(block.id) : ''
			}

			const tmpUid = id ? deletePrefix(id) : getScopeId(range.startContainer)
			const endBlockUid = id ? deletePrefix(id) : getScopeId(range.endContainer)
			
			// cellUid 추출 (테이블 셀인 경우)
			const getCellUid = (node: Node) => {
				const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node as HTMLElement
				if (!el || !(el instanceof HTMLElement)) return undefined
				const td = el.closest('td')
				if (td && !td.classList.contains('number--line') && td.id) {
					return deletePrefix(td.id)
				}
				return undefined
			}
			const cellUid = getCellUid(range.startContainer)

			// 블록 기준 절대 문자 오프셋도 함께 저장
			const resolveBlockRoot = (node: any) => {
				const el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
				if (!el || !(el instanceof HTMLElement)) return selector.value

				// Table Cell handling: Prioritize TD over editor--block
				const td = el.closest('td') as HTMLElement | null
				if (td && !td.classList.contains('number--line')) {
					return (td.querySelector('div[contenteditable="true"]') as HTMLElement) || td
				}

				const block = el.closest('.editor--block') as HTMLElement | null
				if (block) return block

				return selector.value || el
			}
			const startRoot: any = resolveBlockRoot(range.startContainer)
			const endRoot: any = resolveBlockRoot(range.endContainer)
			const preStart = document.createRange()
			preStart.setStart(startRoot, 0)
			preStart.setEnd(range.startContainer, range.startOffset)
			const startAbs = preStart.toString().length
			const preEnd = document.createRange()
			preEnd.setStart(endRoot, 0)
			preEnd.setEnd(range.endContainer, range.endOffset)
			const endAbs = preEnd.toString().length

			changeRange.value = { nodePath, endNodePath, offset: tmpOffset, endOffset: tmpEndOffset, blockUid: tmpUid, endBlockUid, startAbs, endAbs, cellUid }
		}
	}

	public setElement(element: HTMLElement) {
		this.element = element
	}

	// 커서를 가장 뒤로 이동합니다.
	public moveCursorToEnd(b: boolean = false): void {
		if (!this.element) {
			return
		}

		// 포커스를 contenteditable 요소로 이동
		this.element.focus()

		// 현재 브라우저에서 제공하는 Selection API를 사용
		const selection = window.getSelection()
		const range = document.createRange()

		// 요소의 마지막 자식 노드로 커서를 이동
		range.selectNodeContents(this.element)
		range.collapse(b) // 범위를 끝으로 설정

		// 커서 이동 적용
		selection?.removeAllRanges()
		selection?.addRange(range)
	}

	// 커서를 가장 앞으로 이동합니다.
	public moveCursorToStart(): void {
		if (!this.element) {
			return
		}

		// 포커스를 contenteditable 요소로 이동
		this.element.focus()

		// 현재 브라우저에서 제공하는 Selection API를 사용
		const selection = window.getSelection()
		const range = document.createRange()

		// 요소의 시작 위치로 커서를 이동
		range.selectNodeContents(this.element)
		range.collapse(true) // 범위를 시작으로 설정

		// 커서 이동 적용
		selection?.removeAllRanges()
		selection?.addRange(range)
	}

	public insertTextBeforeCursor(v: any): void {
		if (!this.element) {
			return
		}

		const selection: any = window.getSelection()
		const range = selection.getRangeAt(0)

		let nodeToInsert: Node
		if (/<[^>]+>/g.test(v)) {
			nodeToInsert = document.createRange().createContextualFragment(v)
		} else {
			nodeToInsert = document.createTextNode(v)
		}

		range.deleteContents()
		const node = nodeToInsert.firstChild || nodeToInsert
		const clonedNode = node.cloneNode(true)

		range.insertNode(clonedNode)
		if (clonedNode.parentNode) {
			if (clonedNode instanceof Node) {
				range.setStartBefore(clonedNode)
				range.setEndBefore(clonedNode)
			}
			selection.removeAllRanges()
			selection.addRange(range)
		}
	}

	// 블록(this.element) 기준 절대 시작 위치에 커서가 있는지 판단
	// - 형제 span, 중첩 엘리먼트, 분리된 TextNode 모두 고려
	public isCursorAtAbsoluteStart(): boolean {
		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0 || !this.element) {
			return false
		}

		const range = selection.getRangeAt(0)
		// 커서가 대상 블록 내부가 아니면 false
		if (!this.element.contains(range.startContainer)) {
			return false
		}

		// 블록의 "절대 시작"을 가리키는 Range 생성 (첫 자식 이전 지점)
		const startRange = document.createRange()
		startRange.selectNodeContents(this.element)
		startRange.collapse(true)

		// 커서 시작 지점이 블록의 절대 시작과 동일하면 true
		return range.compareBoundaryPoints(Range.START_TO_START, startRange) === 0
	}

	public isCursorAtBlockEnd(blockElement: HTMLElement): boolean {
		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) {
			return false
		}

		const range = selection.getRangeAt(0)

		// 커서가 블록 내부에 없으면 false
		if (!blockElement.contains(range.startContainer)) {
			return false
		}

		// 드래그 등 비-컬랩스 상태는 커서 끝 판단 대상 아님
		if (!selection.isCollapsed) {
			return false
		}

		// 블록의 "절대 끝" Range
		const endRange = document.createRange()
		endRange.selectNodeContents(blockElement)
		endRange.collapse(false) // 끝으로 설정

		// 커서(현재 range)의 시작이 블록의 절대 끝과 동일한지 비교
		return range.compareBoundaryPoints(Range.START_TO_START, endRange) === 0
	}

	public changeBlockFocus(block: HTMLElement) {
		this.element = block
		this.element.focus()
	}

	public changeOffset() {
		const selection: any = window.getSelection()
		const range = document.createRange()

		if (this.targetNode) {
			const v = this.element.innerHTML
			let nodeToText: Node
			if (/<[^>]+>/g.test(v)) {
				nodeToText = document.createRange().createContextualFragment(v)
			} else {
				nodeToText = document.createTextNode(v)
			}

			range.deleteContents()
			range.insertNode(nodeToText)

			range.setStart(this.element, this.targetOffset)
		}
		range.collapse(true)

		selection.removeAllRanges()
		selection.addRange(range)
	}

	public setCursorOffset() {
		const selection: any = window.getSelection()
		const selectionOffset = selection.anchorOffset

		this.traverseNodes(this.element, selectionOffset)
	}

	public traverseNodes(node: Node, offset = 0) {
		if (node.nodeType === Node.TEXT_NODE) {
			const textLength = (node.textContent || '').length

			if (this.currentOffset + textLength >= offset) {
				this.targetNode = node
				this.targetOffset = offset - this.currentOffset
				return true
			}

			this.currentOffset += textLength
		} else if (node.nodeType === Node.ELEMENT_NODE) {
			for (let child of node.childNodes) {
				if (this.traverseNodes(child, offset)) return true
			}
		}
		return false
	}

	public moveCursorToNthCharacter(node: Node, nth: number) {
		const range = document.createRange()
		const selection: any = window.getSelection()

		let currentOffset = 0 // 현재 오프셋 값을 추적

		for (const n of node.childNodes) {
			if (n.nodeType === Node.TEXT_NODE) {
				const textContent = n.textContent || ''
				// 현재 텍스트 노드에 nth 문자(커서)가 위치하는 경우
				const textLength = textContent.length

				if (currentOffset + textLength >= nth) {
					range.setStart(n, nth - currentOffset)
					range.collapse(true)

					selection.removeAllRanges()
					selection.addRange(range)
					return
				}

				currentOffset += textLength // 다음 노드로 오프셋 이동
			} else if (n.nodeType === Node.ELEMENT_NODE) {
				const textContent = n.textContent || ''
				// 현재 텍스트 노드에 nth 문자(커서)가 위치하는 경우
				const textLength = textContent.length

				if (currentOffset + textLength >= nth) {
					const innerNth: number = nth - currentOffset
					this.moveCursorToNthCharacter(n, innerNth) // 재귀적으로 내부 노드로 이동
					return
				}

				currentOffset += textLength
			}
		}

		throw new Error(`The nth character ${nth} exceeds the total text length.`)
	}

	public saveRange(selector: HTMLDivElement | any, text: string = '', type: string = 'insert') {
		const selection: any = window.getSelection()
		const range = document.createRange()
		const { isFunctionActive } = storeToRefs(useEditorStore())

		if (!isFunctionActive.value) return

		if (selection) {
			let tmpText = ''
			let originText = selector?.textContent
			let lastText = ''
			if (selector?.textContent.includes(')')) {
				lastText = ')'
				originText = selector?.textContent?.slice(0, -1)
			}

			if (originText.charAt(originText.length - 1) === '(' || originText.charAt(originText.length - 1) === ',') {
				tmpText = originText + text + lastText
			} else {
				if (type === 'shift') {
					tmpText = originText + ':' + text + lastText
				} else {
					tmpText = originText + ',' + text + lastText
				}
			}

			selector.innerHTML = tmpText

			let textLength = selector?.textContent?.length

			if (selector?.textContent.includes(')')) {
				textLength -= 1
			}

			range.setStart(selector.firstChild, textLength)
			range.collapse(true)

			const useDelete = useDeleteTable()

			useDelete.functionList()

			const selection = window.getSelection()

			if (selection) {
				selection.removeAllRanges()
				selection.addRange(range)
			}
		}
	}

	public getCharBeforeCursor(container: HTMLDivElement, offset: number) {
		if (offset > 0) {
			// container의 텍스트 내용에서 offset 바로 앞의 문자를 가져옵니다.
			return container.textContent?.[offset - 1]
		}

		// 그 외의 경우 (커서가 맨 앞이거나, 텍스트 노드가 아닌 경우)
		return null
	}

	public isCursorAtBlockStart(blockElement: HTMLElement): boolean {
		const selection: any = window.getSelection()
		if (!selection || selection.rangeCount === 0) return false

		const { selector } = storeToRefs(useEditorStore())

		const { anchorNode } = selection

		if (!blockElement) {
			blockElement = anchorNode.nodeType === Node.TEXT_NODE ? anchorNode.parentNode.closest('.editor--block') : anchorNode.closest('.editor--block')
			selector.value = blockElement
		}

		let node: any = selection.anchorNode
		let offset = selection.anchorOffset

		if (!blockElement.contains(node)) return false

		if (node.nodeType === Node.TEXT_NODE) {
			if (offset === 0) {
				let current: Node | null = node
				while (current && current !== blockElement) {
					if (current.previousSibling) return false
					current = current.parentNode
				}
				return true
			}
			return false
		} else if (node === blockElement) {
			return offset === 0
		} else {
			return offset === 0 && !node.previousSibling
		}
	}

	private getMarkerRect(range: Range): DOMRect {
		const marker = document.createElement('span')
		marker.style.cssText = 'display:inline;width:0;height:0;overflow:hidden;position:static;'
		range.insertNode(marker)
		const rect = marker.getBoundingClientRect()
		marker.parentNode?.removeChild(marker)
		return rect
	}

	public isCursorOnFirstLine(blockElement: HTMLElement): boolean {
		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) return true

		const range = selection.getRangeAt(0)
		if (!blockElement.contains(range.startContainer)) return true

		const cursorRange = range.cloneRange()
		cursorRange.collapse(true)
		const cursorRect = this.getMarkerRect(cursorRange)

		const firstRange = document.createRange()
		firstRange.selectNodeContents(blockElement)
		firstRange.collapse(true)
		const firstRect = this.getMarkerRect(firstRange)

		return Math.abs(cursorRect.top - firstRect.top) < 5
	}

	public isCursorOnLastLine(blockElement: HTMLElement): boolean {
		const selection = window.getSelection()
		if (!selection || selection.rangeCount === 0) return true

		const range = selection.getRangeAt(0)
		if (!blockElement.contains(range.startContainer)) return true

		const cursorRange = range.cloneRange()
		cursorRange.collapse(true)
		const cursorRect = this.getMarkerRect(cursorRange)

		const lastRange = document.createRange()
		lastRange.selectNodeContents(blockElement)
		lastRange.collapse(false)
		const lastRect = this.getMarkerRect(lastRange)

		return Math.abs(cursorRect.top - lastRect.top) < 5
	}

	public isCursorCheckPoint() {
		const { selector, changeRange } = storeToRefs(useEditorStore())
		try {
			const selection: any = window.getSelection()
			if (selection && changeRange.value && selector.value) {
				selector.value.focus()

				const { nodePath, endNodePath, offset, endOffset, blockUid, endBlockUid } = changeRange.value
				const resolveDomByUid = (uid: string, fallback: HTMLElement) => {
					let el: any = document.getElementById(`prefix-${uid}`) || document.getElementById(uid) || fallback
					// TD가 루트로 넘어오는 경우, 셀 내부의 contenteditable DIV를 루트로 보정
					if (el && el.tagName === 'TD') {
						const inner = el.querySelector('div[contenteditable="true"]')
						if (inner) el = inner
					}
					return el as HTMLElement
				}
				const tempDom = resolveDomByUid(blockUid, selector.value)
				const endTempDom = resolveDomByUid(endBlockUid, selector.value)
				let targetNode: any = getNodeFromPath(tempDom, nodePath)
				let endTargetNode: any = getNodeFromPath(endTempDom, endNodePath)
				if (!targetNode) {
					nodePath.shift()
					targetNode = getNodeFromPath(tempDom, nodePath)
				}

				if (!endTargetNode) {
					endNodePath.shift()
					endTargetNode = getNodeFromPath(endTempDom, endNodePath)
				}

				// 경로 복원이 실패하거나 요소 노드인 경우 절대 오프셋 기반으로 재탐색
				const findNodeByNth = (root: Node, nth: number): { node: Node; offset: number } => {
					let currentOffset = 0
					for (const n of root.childNodes || []) {
						if (n.nodeType === Node.TEXT_NODE) {
							const len = n.textContent?.length || 0
							if (currentOffset + len >= nth) {
								return { node: n, offset: nth - currentOffset }
							}
							currentOffset += len
						} else if (n.nodeType === Node.ELEMENT_NODE) {
							const len = n.textContent?.length || 0
							if (currentOffset + len >= nth) {
								// 재귀적으로 내부 탐색
								return findNodeByNth(n, nth - currentOffset)
							}
							currentOffset += len
						}
					}
					// 텍스트가 전혀 없으면 루트 끝으로
					return { node: root, offset: root.childNodes?.length || 0 }
				}

				if (!targetNode || targetNode.nodeType !== Node.TEXT_NODE) {
					const nth = changeRange.value.startAbs || 0
					const res = findNodeByNth(tempDom, nth)
					targetNode = res.node
					// offset을 절대 오프셋으로 보정할 수도 있지만, 일단 저장된 상대 offset을 사용
				}
				if (!endTargetNode || endTargetNode.nodeType !== Node.TEXT_NODE) {
					const nth = changeRange.value.endAbs || 0
					const res = findNodeByNth(endTempDom, nth)
					endTargetNode = res.node
				}

				targetNode.parentElement.focus()
				
				const editorBlock = targetNode.parentElement.closest('.editor--block') as HTMLElement
				if (editorBlock && !targetNode.parentElement.closest('td') && editorBlock.dataset.type !== 'defaultTable') {
					document.querySelectorAll('.editor--block.active').forEach(el => {
						if (el !== editorBlock) {
							el.classList.remove('active')
						}
					})
					editorBlock.classList.add('active')
				}

				const startAbs = changeRange.value.startAbs
				const endAbs = changeRange.value.endAbs

				selection.removeAllRanges()
				const hasAbsoluteOffsets = typeof startAbs === 'number' && typeof endAbs === 'number'
				
				if (hasAbsoluteOffsets) {
					const sRes = findNodeByNth(tempDom, startAbs)
					const eRes = findNodeByNth(endTempDom, endAbs)
					if (typeof selection.setBaseAndExtent === 'function') {
						selection.setBaseAndExtent(sRes.node, sRes.offset, eRes.node, eRes.offset)
					} else {
						const r2 = document.createRange()
						r2.setStart(sRes.node, sRes.offset)
						r2.setEnd(eRes.node, eRes.offset)
						selection.addRange(r2)
					}
				} else {
					const range = document.createRange()
					const sLen = targetNode.nodeType === Node.TEXT_NODE ? targetNode.textContent?.length || 0 : 0
					const eLen = endTargetNode.nodeType === Node.TEXT_NODE ? endTargetNode.textContent?.length || 0 : 0
					const sOff = Math.max(0, Math.min(offset, sLen))
					const eOff = Math.max(0, Math.min(endOffset, eLen))
					if (offset !== endOffset || targetNode !== endTargetNode) {
						range.setStart(targetNode, sOff)
						range.setEnd(endTargetNode, eOff)
					} else {
						range.setStart(targetNode, sOff)
						range.collapse(false)
					}

					selection.addRange(range)
				}
			} else {
				const range = document.createRange()
				range.setStart(selector.value, 0)
				range.collapse(false)
				selection.removeAllRanges()
				selection.addRange(range)
			}
		} catch (error) {
		}
	}
}
